import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "motion/react";

import { LanguageProvider, useLang } from "./i18n/LanguageContext";
import { ThemeProvider } from "./theme/ThemeContext";
import { AuthProvider } from "./auth/AuthContext";
import { pageVariants } from "./lib/motion-variants";

import Layout from "./components/layout/Layout";
import Analytics from "./components/global/Analytics";

// Disable the browser's own scroll-restoration so a refresh/reload never
// reopens the page mid-scroll — it can reapply asynchronously (after fonts/
// chunks finish loading) and win a race against any scrollTo() called from
// React. Runs at module eval time, before React mounts.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
  window.scrollTo(0, 0);
}

// Home is the most likely first paint — kept eager. Every other route is a
// separate chunk so public visitors never download the admin bundle (or
// each other's pages) up front.
import Home from "./pages/Home";
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Industries = lazy(() => import("./pages/Industries"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const StartProject = lazy(() => import("./pages/StartProject"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AnimationDemo = lazy(() => import("./pages/AnimationDemo"));

const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminAssessments = lazy(() => import("./pages/admin/AdminAssessments"));
const AdminAssessmentDetail = lazy(() => import("./pages/admin/AdminAssessmentDetail"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminCaseStudies = lazy(() => import("./pages/admin/AdminCaseStudies"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminIndustries = lazy(() => import("./pages/admin/AdminIndustries"));

function RouteFallback() {
  return <div className="min-h-[40vh]" aria-hidden="true" />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function PageTransition({ children }) {
  const { pathname } = useLocation();
  const { dir } = useLang();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={pathname} variants={pageVariants(dir)} initial="initial" animate="animate" exit="exit">
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function PublicSite() {
  return (
    <Layout>
      <PageTransition>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<CaseStudyDetail />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/start-project" element={<StartProject />} />
            <Route path="/animation-demo" element={<AnimationDemo />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </PageTransition>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Analytics />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Admin area — plain chrome, no site Header/Footer */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverview />} />
                    <Route path="contacts" element={<AdminContacts />} />
                    <Route path="assessments" element={<AdminAssessments />} />
                    <Route path="assessments/:id" element={<AdminAssessmentDetail />} />
                    <Route path="newsletter" element={<AdminNewsletter />} />
                    <Route path="blog" element={<AdminBlog />} />
                    <Route path="testimonials" element={<AdminTestimonials />} />
                    <Route path="case-studies" element={<AdminCaseStudies />} />
                    <Route path="services" element={<AdminServices />} />
                    <Route path="industries" element={<AdminIndustries />} />
                  </Route>
                  {/* Public site */}
                  <Route path="/*" element={<PublicSite />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: "var(--glass-strong-bg)",
                  border: "1px solid rgba(0,229,255,0.25)",
                  color: "var(--text-strong)",
                  backdropFilter: "blur(20px)",
                },
              }}
            />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
